import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faFilterList } from "@fortawesome/pro-regular-svg-icons";
import useExporter from "../../hooks/useExporter";
import { useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getManagedDevicesByAgreement } from "../../api/Agreement";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import TableView from "../../components/TableView";
import MultipleSelectDropdown from "../../components/MultipleSelectDropdown";

const DeviceInventoryPage = () => {
  const { agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const { handleExport } = useExporter();

  const [devices, setDevices] = useState([]);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const modalRef = useRef();

  const filterColumns = [
    { key: "primaryUserDisplayName", label: "Primary user" },
    { key: "manufacturer", label: "Manufacturer" },
    { key: "model", label: "Model" },
    { key: "serialNumber", label: "Serial number" },
    { key: "cpuModel", label: "CPU" },
    { key: "ramFormatted", label: "RAM" },
    { key: "totalStorageFormatted", label: "Total storage" },
    { key: "freeStorageFormatted", label: "Free storage" },
    { key: "deviceName", label: "Device name" },
    { key: "primaryUserEmail", label: "Primary user email" },
    { key: "enrolledFormatted", label: "Enrollment date" },
    { key: "osVersion", label: "OS version" },
    { key: "lastSyncFormatted", label: "Last check-in" },
    { key: "complianceState", label: "Compliance" },
    { key: "encryptedFormatted", label: "Encrypted" },
    { key: "joinType", label: "Join type" },
  ];

  const getFilterOptions = (key) =>
    [...new Set(devices.map((d) => d[key]).filter(Boolean))]
      .sort()
      .map((v) => ({ label: v, value: v }));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen]);

  const handleExportToExcel = () => {
    const devicesToExport =
      selectedDevices.length > 0
        ? filteredDevices.filter((d) =>
            selectedDevices.includes(d.managedDevicePublicId)
          )
        : filteredDevices;

    if (devicesToExport.length === 0) return;

    const data = devicesToExport.map((d) => ({
      "Primary user": d.primaryUserDisplayName,
      "Manufacturer": d.manufacturer,
      "Model": d.model,
      "Serial number": d.serialNumber,
      "CPU": d.cpuModel,
      "RAM": d.ramFormatted,
      "Total storage": d.totalStorageFormatted,
      "Free storage": d.freeStorageFormatted,
      "Device name": d.deviceName,
      "Primary user email": d.primaryUserEmail,
      "Enrollment date": d.enrolledFormatted,
      "OS version": d.osVersion,
      "Last check-in": d.lastSyncFormatted,
      "Compliance": d.complianceState,
      "Encrypted": d.encryptedFormatted,
      "Join type": d.joinType,
    }));

    const currentDate = new Date().toISOString().split("T")[0];
    handleExport({ "Device Inventory": data }, `DeviceInventory_${currentDate}`);
  };

  useEffect(() => {
    handleGetDevices();
  }, [agreementPublicId]);

  useEffect(() => {
    let filtered = devices;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = devices.filter(
        (d) =>
          d.deviceName?.toLowerCase().includes(searchLower) ||
          d.primaryUserDisplayName?.toLowerCase().includes(searchLower) ||
          d.primaryUserEmail?.toLowerCase().includes(searchLower) ||
          d.operatingSystem?.toLowerCase().includes(searchLower) ||
          d.model?.toLowerCase().includes(searchLower) ||
          d.manufacturer?.toLowerCase().includes(searchLower) ||
          d.serialNumber?.toLowerCase().includes(searchLower) ||
          d.cpuModel?.toLowerCase().includes(searchLower)
      );
    }

    filterColumns.forEach(({ key }) => {
      if (filters[key]?.length > 0) {
        filtered = filtered.filter((d) => filters[key].includes(d[key]));
      }
    });

    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = (a[sortColumn] || "").toString().toLowerCase();
        const bValue = (b[sortColumn] || "").toString().toLowerCase();
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    }

    setFilteredDevices(filtered);
  }, [search, devices, sortColumn, sortOrder, filters]);

  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString() : "-";

  const formatStorage = (bytes) =>
    bytes ? (bytes / 1073741824).toFixed(1) + " GB" : "-";

  const handleGetDevices = async () => {
    try {
      const accessToken = await getAccessToken();
      const result = await getManagedDevicesByAgreement(agreementPublicId, accessToken);

      result.forEach((device) => {
        device.enrolledFormatted = formatDate(device.enrolledDateTime);
        device.lastSyncFormatted = formatDate(device.lastSyncDateTime);
        device.totalStorageFormatted = formatStorage(device.totalStorageSpaceInBytes);
        device.freeStorageFormatted = formatStorage(device.freeStorageSpaceInBytes);
        device.ramFormatted = formatStorage(device.physicalMemoryInBytes);
        device.encryptedFormatted = device.isEncrypted != null
          ? (device.isEncrypted ? "Yes" : "No") : "-";
      });

      setDevices(result);
      setFilteredDevices(result);
    } catch (error) {
      console.error("Failed to fetch managed devices:", error);
    }
    setDevicesLoaded(true);
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Device inventory"} loading={!devicesLoaded} />
            </div>

            <div className={`${!devicesLoaded ? "loading" : ""} page-content pb-[25rem]`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Device inventory</h2>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <div></div>
                  <div className="flex items-center">
                    <div className="relative">
                      <button
                        className="btn-outline-primary-small mr-4 mt-2 icon"
                        onClick={() => setIsFilterOpen((prev) => !prev)}
                      >
                        <FontAwesomeIcon icon={faFilterList} className="mr-2 text-lg" />
                        Filter
                      </button>
                      {isFilterOpen && (
                        <div className="absolute top-12 z-10" ref={modalRef}>
                          <div className="p-4 bg-white flex gap-4 flex-col w-[22rem] border rounded-xl max-h-[70vh] overflow-y-auto">
                            <h3>Filter devices</h3>

                            {filterColumns.map(({ key, label }) => (
                              <MultipleSelectDropdown
                                key={key}
                                label={label}
                                options={getFilterOptions(key)}
                                selected={filters[key] || []}
                                onChange={(selected) =>
                                  setFilters((prev) => ({ ...prev, [key]: selected }))
                                }
                              />
                            ))}

                            <button
                              className="btn-outline-primary-small"
                              onClick={() => setFilters({})}
                            >
                              Clear filter
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"Name, User, OS, Model"}
                      value={search}
                      onChange={(v) => setSearch(v.target.value)}
                    />
                    <button
                      className="btn-outline-primary-small ml-4 mt-2"
                      onClick={handleExportToExcel}
                    >
                      Export to excel
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredDevices}
                    selectedRows={selectedDevices}
                    setSelectedRows={setSelectedDevices}
                    noCheckbox={true}
                    columns={[
                      { key: "primaryUserDisplayName", label: "Primary user" },
                      { key: "manufacturer", label: "Manufacturer" },
                      { key: "model", label: "Model" },
                      { key: "serialNumber", label: "Serial number" },
                      { key: "cpuModel", label: "CPU" },
                      { key: "ramFormatted", label: "RAM" },
                      { key: "totalStorageFormatted", label: "Total storage" },
                      { key: "freeStorageFormatted", label: "Free storage" },
                      { key: "deviceName", label: "Device name" },
                      { key: "primaryUserEmail", label: "Primary user email" },
                      { key: "enrolledFormatted", label: "Enrollment date" },
                      { key: "osVersion", label: "OS version" },
                      { key: "lastSyncFormatted", label: "Last check-in" },
                      { key: "complianceState", label: "Compliance" },
                      { key: "encryptedFormatted", label: "Encrypted" },
                      { key: "joinType", label: "Join type" },
                    ]}
                    primaryKey="managedDevicePublicId"
                    basePath={`/${agreementPublicId}/device-inventory`}
                    noDelete={true}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default DeviceInventoryPage;
